
import React, { useEffect, useRef, useState } from 'react';
import {
    getTelnyxNumbers,
    getWhatsAppNumbers,
    assignTelnyxNumber,
    assignWhatsAppNumber,
    activateWhatsAppNumber,
    unassignWhatsAppNumber,
    getAdminMe,
    TelnyxNumber,
    WhatsAppNumber
} from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { Building2, Check, Phone, MessageSquare, X, Zap, CreditCard, History, RefreshCw, Trash2 } from 'lucide-react';
import api from '../services/api.ts';

// Types for Credits
interface ClinicCredits {
    clinic_id: number;
    clinic_name: string;
    whatsapp_credits: number;
    has_official_whatsapp: boolean;
}

interface CreditTransaction {
    id: number;
    clinic_id: number;
    amount: number;
    balance_after: number;
    transaction_type: string;
    description: string | null;
    plan_name: string | null;
    created_at: string;
}

const AdminResources: React.FC = () => {
    const { isDark } = useTheme();
    const [activeTab, setActiveTab] = useState<'telnyx' | 'whatsapp' | 'credits'>('telnyx');
    const [telnyxNumbers, setTelnyxNumbers] = useState<TelnyxNumber[]>([]);
    const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
    const [loading, setLoading] = useState(false);

    // Dialog state
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [selectedResourceType, setSelectedResourceType] = useState<'telnyx' | 'whatsapp' | null>(null);
    const [selectedResource, setSelectedResource] = useState<any>(null);
    const [targetComponentClinicId, setTargetComponentClinicId] = useState<number | ''>('');
    const [wabaIdInput, setWabaIdInput] = useState('');
    const [isActivating, setIsActivating] = useState(false);

    // Credits state
    const [clinicsCredits, setClinicsCredits] = useState<ClinicCredits[]>([]);
    const [selectedClinicForCredits, setSelectedClinicForCredits] = useState<ClinicCredits | null>(null);
    const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
    const [loadingCredits, setLoadingCredits] = useState(false);
    const [showTransactionsModal, setShowTransactionsModal] = useState(false);
    const [creditsError, setCreditsError] = useState<string | null>(null);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [transactionsError, setTransactionsError] = useState<string | null>(null);
    const historyDialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!showTransactionsModal) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        historyDialogRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setShowTransactionsModal(false);
            if (event.key === 'Tab') {
                event.preventDefault();
                historyDialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previousFocus?.focus();
        };
    }, [showTransactionsModal]);

    useEffect(() => {
        fetchResources();
    }, []);

    const fetchResources = async () => {
        setLoading(true);

        // Auto-fetch Clinic ID on load
        try {
            const me = await getAdminMe();
            if (me && me.clinic_id) {
                setTargetComponentClinicId(me.clinic_id);
            }
        } catch (error) {
            console.error("Failed to fetch current user info", error);
        }

        // Telnyx
        try {
            const tNums = await getTelnyxNumbers();
            setTelnyxNumbers(tNums);
        } catch (error) {
            console.error("Failed to fetch Telnyx resources", error);
        }

        // WhatsApp
        try {
            const wNums = await getWhatsAppNumbers();
            setWhatsappNumbers(wNums);
        } catch (error) {
            console.error("Failed to fetch WhatsApp resources", error);
        }

        // Credits
        await fetchCreditsData();
        setLoading(false);
    };

    const fetchCreditsData = async () => {
        setLoadingCredits(true);
        setCreditsError(null);
        try {
            const clinicsRes = await api.get('/api/admin/credits/clinics');
            setClinicsCredits(clinicsRes.data);
        } catch (error) {
            setCreditsError('Erro ao carregar o histórico de créditos. Tente atualizar novamente.');
        }
        setLoadingCredits(false);
    };

    const fetchTransactions = async (clinicId: number) => {
        setLoadingTransactions(true);
        setTransactionsError(null);
        setTransactions([]);
        try {
            const res = await api.get(`/api/admin/credits/transactions/${clinicId}`);
            setTransactions(res.data);
        } catch (error) {
            setTransactionsError('Erro ao carregar as transações. Tente abrir o histórico novamente.');
        } finally {
            setLoadingTransactions(false);
        }
    };

    const openTransactionsModal = async (clinic: ClinicCredits) => {
        setSelectedClinicForCredits(clinic);
        setShowTransactionsModal(true);
        await fetchTransactions(clinic.clinic_id);
    };

    const handleAssignClick = (type: 'telnyx' | 'whatsapp', resource: any) => {
        setSelectedResourceType(type);
        setSelectedResource(resource);
        setWabaIdInput(''); // Reset WABA ID
        setOpenAssignDialog(true);
    };

    const handleSaveAssign = async () => {
        if (!targetComponentClinicId) return;

        try {
            if (selectedResourceType === 'telnyx') {
                await assignTelnyxNumber(selectedResource.phone_number, Number(targetComponentClinicId));
            } else {
                await assignWhatsAppNumber(selectedResource.id, wabaIdInput, Number(targetComponentClinicId));
            }

            setOpenAssignDialog(false);
            fetchResources(); // Refresh
            alert("Atribuído com sucesso!");
        } catch (error) {
            console.error(error);
            alert("Erro ao atribuir. Verifique o ID da clínica.");
        }
    };

    const handleActivateWhatsApp = async () => {
        if (!targetComponentClinicId) {
            alert("Erro: Clínica não identificada");
            return;
        }

        setIsActivating(true);
        try {
            const result = await activateWhatsAppNumber(
                selectedResource.id,
                Number(targetComponentClinicId)
            );
            setOpenAssignDialog(false);
            fetchResources();
            alert(`✅ ${result.message}`);
        } catch (error: any) {
            console.error(error);
            const errorMsg = error?.response?.data?.detail || "Erro ao ativar número";
            alert(`❌ ${errorMsg}`);
        } finally {
            setIsActivating(false);
        }
    };

    const handleUnassignWhatsApp = async () => {
        if (!targetComponentClinicId) return;
        const ok = window.confirm(
            '⚠️ Tem certeza que deseja remover a atribuição do WhatsApp Business desta clínica?\n\nIsso irá desconectar o número oficial.'
        );
        if (!ok) return;

        try {
            const result = await unassignWhatsAppNumber(Number(targetComponentClinicId));
            alert(`✅ ${result.message}`);
            fetchResources();
        } catch (error: any) {
            console.error('[Unassign WA]', error);
            const errMsg = error?.response?.data?.detail || 'Erro ao desatribuir número';
            alert(`❌ ${errMsg}`);
        }
    };

    return (
        <div className={`p-6 min-h-screen ${isDark ? 'text-gray-100 bg-slate-950' : 'text-gray-800 bg-gray-50'}`}>
            <div className="max-w-7xl mx-auto">
                <h1 className="text-2xl font-bold mb-6">Gestão de Recursos Oficiais</h1>

                {/* Tabs */}
                <div className="flex space-x-4 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                    <button
                        className={`py-2 px-4 font-medium transition-colors ${activeTab === 'telnyx'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                        onClick={() => setActiveTab('telnyx')}
                    >
                        Números Telnyx
                    </button>
                    <button
                        className={`py-2 px-4 font-medium transition-colors ${activeTab === 'whatsapp'
                            ? 'border-b-2 border-blue-500 text-blue-500'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                        onClick={() => setActiveTab('whatsapp')}
                    >
                        WhatsApp Business API
                    </button>
                    <button
                        className={`py-2 px-4 font-medium transition-colors flex items-center gap-2 ${activeTab === 'credits'
                            ? 'border-b-2 border-green-500 text-green-500'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                        onClick={() => setActiveTab('credits')}
                    >
                        <CreditCard className="w-4 h-4" />
                        Créditos WhatsApp
                    </button>
                </div>

                {/* Content */}
                <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden`}>
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Carregando...</div>
                    ) : activeTab === 'credits' ? (
                        /* Credits Tab Content */
                        <div className={`p-4 sm:p-6 ${isDark ? 'bg-sminy-dark-surface text-sminy-dark-text-primary' : 'bg-white text-sminy-text-primary'}`}>
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold">Histórico de créditos</h2>
                                    <p className={`text-sm mt-1 ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>Saldos e transações anteriores são preservados.</p>
                                </div>
                                <button
                                    onClick={fetchCreditsData}
                                    disabled={loadingCredits}
                                    className={`min-h-11 flex items-center gap-2 px-4 py-2 text-sm rounded-xl border focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'border-sminy-dark-border bg-sminy-dark-surface hover:bg-sminy-dark-surface-subtle' : 'border-sminy-border hover:bg-sminy-blue-soft'}`}
                                >
                                    <RefreshCw className={`w-4 h-4 ${loadingCredits ? 'animate-spin' : ''}`} />
                                    Atualizar
                                </button>
                            </div>

                            <p className={`mb-6 p-4 rounded-xl border text-xs ${isDark ? 'bg-sminy-dark-surface-subtle border-sminy-dark-border text-sminy-dark-text-secondary' : 'bg-sminy-blue-soft border-sminy-border text-sminy-text-secondary'}`}>
                                As tarifas do WhatsApp Oficial são cobradas diretamente pela Meta.
                            </p>
                            {creditsError && <p role="alert" className={`mb-4 text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{creditsError}</p>}

                            {/* Clinics Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs sm:text-sm">
                                    <thead className={`border-b ${isDark ? 'bg-sminy-dark-surface-subtle border-sminy-dark-border text-sminy-dark-text-secondary' : 'bg-sminy-blue-soft border-sminy-border text-sminy-text-secondary'}`}>
                                        <tr>
                                            <th className="px-2 sm:px-6 py-3 text-xs font-medium">Clínica</th>
                                            <th className="px-2 sm:px-6 py-3 text-xs font-medium"><span className="sm:hidden">Oficial</span><span className="hidden sm:inline">WhatsApp Oficial</span></th>
                                            <th className="px-2 sm:px-6 py-3 text-xs font-medium"><span className="sm:hidden">Saldo</span><span className="hidden sm:inline">Saldo histórico</span></th>
                                            <th className="px-2 sm:px-6 py-3 text-xs font-medium">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDark ? 'divide-sminy-dark-border' : 'divide-sminy-border'}`}>
                                        {loadingCredits ? (
                                            <tr><td colSpan={4} className="px-2 sm:px-2 sm:px-6 py-4 text-center" role="status">Carregando histórico...</td></tr>
                                        ) : clinicsCredits.filter(c => c.clinic_id === targetComponentClinicId).length === 0 ? (
                                            creditsError ? null : <tr><td colSpan={4} className="px-2 sm:px-6 py-4 text-center">Nenhuma clínica encontrada</td></tr>
                                        ) : (
                                            clinicsCredits.filter(c => c.clinic_id === targetComponentClinicId).map((clinic) => (
                                                <tr key={clinic.clinic_id} className={isDark ? 'hover:bg-sminy-dark-surface-subtle' : 'hover:bg-sminy-blue-soft'}>
                                                    <td className="px-2 sm:px-6 py-4">
                                                        <div className="font-medium">{clinic.clinic_name}</div>
                                                        <div className="text-xs text-gray-400">ID: {clinic.clinic_id}</div>
                                                    </td>
                                                    <td className="px-2 sm:px-6 py-4">
                                                        {clinic.has_official_whatsapp ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                                                <Check className="w-3 h-3" /> Ativo
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">Não configurado</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 sm:px-6 py-4">
                                                        <span className={`text-base sm:text-xl font-semibold ${isDark ? 'text-sminy-dark-text-primary' : 'text-sminy-text-primary'}`}>
                                                            {clinic.whatsapp_credits}
                                                        </span>
                                                        <span className="hidden sm:inline text-sm text-gray-500 ml-1">créditos</span>
                                                    </td>
                                                    <td className="px-2 sm:px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => openTransactionsModal(clinic)}
                                                                className={`min-h-11 flex items-center gap-1 px-2 sm:px-3 py-1.5 border rounded-xl text-sm focus-visible:ring-2 focus-visible:ring-sminy-primary ${isDark ? 'border-sminy-dark-border hover:bg-sminy-dark-surface-subtle' : 'border-sminy-border hover:bg-sminy-blue-soft'}`}
                                                            >
                                                                <History className="w-4 h-4" /> Histórico
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <p className={`mt-6 text-xs ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>Registros anteriores disponíveis para consulta.</p>
                        </div>
                    ) : (
                        /* Telnyx / WhatsApp Tab Content */
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className={`bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700`}>
                                    <tr>
                                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Identificador</th>
                                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status/Detalhes</th>
                                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Atribuído a</th>
                                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {activeTab === 'telnyx' ? (
                                        telnyxNumbers.length === 0 ? (
                                            <tr><td colSpan={4} className="px-6 py-4 text-center">Nenhum número encontrado</td></tr>
                                        ) : (
                                            telnyxNumbers.map((num) => (
                                                <tr key={num.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-medium">{num.phone_number}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${num.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                            {num.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {num.assigned_to ? (
                                                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                                                                <Building2 className="w-3 h-3" />
                                                                {num.assigned_to.clinic_name} (ID: {num.assigned_to.clinic_id})
                                                            </span>
                                                        ) : <span className="text-gray-400 italic">Disponível</span>}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <button
                                                            onClick={() => handleAssignClick('telnyx', num)}
                                                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200 text-sm font-medium"
                                                        >
                                                            Atribuir
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )
                                    ) : (
                                        whatsappNumbers.length === 0 ? (
                                            <tr><td colSpan={4} className="px-6 py-4 text-center">Nenhum número encontrado</td></tr>
                                        ) : (
                                            whatsappNumbers.map((num) => (
                                                <tr key={num.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-medium">{num.verified_name}</div>
                                                        <div className="text-sm text-gray-500">{num.display_phone_number}</div>
                                                        <div className="text-xs text-gray-400 font-mono">{num.id}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${num.quality_rating === 'GREEN' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                            Qualidade: {num.quality_rating}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {num.assigned_to ? (
                                                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                                                                <Building2 className="w-3 h-3" />
                                                                {num.assigned_to.clinic_name} (ID: {num.assigned_to.clinic_id})
                                                            </span>
                                                        ) : <span className="text-gray-400 italic">Disponível</span>}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            {num.assigned_to ? (
                                                                <button
                                                                    onClick={handleUnassignWhatsApp}
                                                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    Desatribuir
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleAssignClick('whatsapp', num)}
                                                                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200 text-sm font-medium"
                                                                >
                                                                    Atribuir
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal / Dialog */}
            {openAssignDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className={`w-full max-w-md p-6 rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                        <h3 className="text-lg font-medium mb-4">Confirmar Atribuição</h3>
                        <p className="mb-4 text-sm text-gray-500">
                            Deseja vincular este número à sua clínica (ID: <strong>{targetComponentClinicId}</strong>)?
                        </p>

                        <div className="space-y-4">
                            {/* Hidden Clinic ID Input - Auto Assigned */}

                            {selectedResourceType === 'whatsapp' && (
                                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <Zap className="w-5 h-5 text-green-500" />
                                        <span className="font-medium">Ativação Automática</span>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        O sistema irá:
                                    </p>
                                    <ul className="text-sm text-gray-500 list-disc list-inside mt-2 space-y-1">
                                        <li>Registrar o número na API Meta</li>
                                        <li>Salvar as credenciais no banco de dados</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setOpenAssignDialog(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
                            >
                                Cancelar
                            </button>
                            {selectedResourceType === 'whatsapp' ? (
                                <button
                                    onClick={handleActivateWhatsApp}
                                    disabled={isActivating}
                                    className={`px-4 py-2 text-sm font-medium text-white rounded flex items-center gap-2 ${isActivating
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 hover:bg-green-700'
                                        }`}
                                >
                                    <Zap className="w-4 h-4" />
                                    {isActivating ? 'Ativando...' : 'Ativar Número'}
                                </button>
                            ) : (
                                <button
                                    onClick={handleSaveAssign}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                >
                                    Confirmar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Transactions History Modal */}
            {showTransactionsModal && selectedClinicForCredits && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <div ref={historyDialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Histórico de Transações" style={{ maxHeight: 'calc(100dvh - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)))' }} className={`w-full max-w-2xl p-4 sm:p-6 rounded-2xl shadow-xl overflow-hidden flex flex-col outline-none ${isDark ? 'bg-sminy-dark-elevated text-sminy-dark-text-primary' : 'bg-white text-sminy-text-primary'}`}>
                        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                            <History className="w-5 h-5 text-sminy-primary" />
                            Histórico de Transações
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Clínica: <strong>{selectedClinicForCredits.clinic_name}</strong>
                        </p>

                        <div className="overflow-auto flex-1 min-h-0">
                            {loadingTransactions ? (
                                <p role="status" className="text-center py-8 text-sm">Carregando transações...</p>
                            ) : transactionsError ? (
                                <p role="alert" className={`py-4 text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>{transactionsError}</p>
                            ) : transactions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    Nenhuma transação encontrada
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className={`sticky top-0 ${isDark ? 'bg-sminy-dark-surface-subtle' : 'bg-sminy-blue-soft'}`}>
                                        <tr>
                                            <th className="px-4 py-2 text-left">Data</th>
                                            <th className="px-4 py-2 text-left">Tipo</th>
                                            <th className="px-4 py-2 text-right">Qtd</th>
                                            <th className="px-4 py-2 text-right">Saldo</th>
                                            <th className="px-4 py-2 text-left">Descrição</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                        {transactions.map(tx => (
                                            <tr key={tx.id}>
                                                <td className="px-4 py-2 whitespace-nowrap">
                                                    {new Date(tx.created_at).toLocaleDateString('pt-BR')}
                                                </td>
                                                <td className="px-4 py-2">
                                                    <span className={`px-2 py-0.5 rounded text-xs ${tx.transaction_type === 'purchase' ? 'bg-green-100 text-green-800' :
                                                        tx.transaction_type === 'consumption' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {tx.transaction_type === 'purchase' ? 'Compra' :
                                                            tx.transaction_type === 'consumption' ? 'Consumo' :
                                                                tx.transaction_type === 'adjustment' ? 'Ajuste' : tx.transaction_type}
                                                    </span>
                                                </td>
                                                <td className={`px-4 py-2 text-right font-medium ${tx.amount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                                                </td>
                                                <td className="px-4 py-2 text-right">{tx.balance_after}</td>
                                                <td className="px-4 py-2 text-gray-500 text-xs">
                                                    {tx.description || tx.plan_name || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => setShowTransactionsModal(false)}
                                className={`min-h-11 px-4 py-2 border rounded-xl text-sm font-medium focus-visible:ring-2 focus-visible:ring-sminy-primary ${isDark ? 'border-sminy-dark-border hover:bg-sminy-dark-surface-subtle' : 'border-sminy-border hover:bg-sminy-blue-soft'}`}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminResources;
